import { useEffect, useState, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Box, Typography } from '@mui/material';
import { getCurrencyFormat, getFloat } from '@utils/regex';
import { DollarSign, InfoCircle, Times } from '@assets/svgs';
import { DataType } from '@services/data/productModelTypes';
import { CollapsedField } from '@components/quoteDetails/CollapsedField';
import { QFCTypeExecuted } from '@components/quoteDetails/QuoteFieldControl';
import { InputLabelContainer } from '@components/container/InputLabelContainer';
import { FormHelperText } from '@components/formHelperText/FormHelperText';
import { TextField } from '@components/textField/TextField';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { getMaskedStyles, StyledIMaskInput } from '../QuoteDetails.styles';
import { Popover } from '@components/popover/Popover';
import { Plus } from '@assets/svgs';
import { IconButton } from '@components/iconButton';
import { Tooltip } from '@components/tooltip';
import { NAMED_INSURED } from '@constants/namedInsured';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { FontSize } from '@store/types/UserStoreSlice.type';
import { adjustLineHeight } from '@theme/styleOverrides/typography';

export interface TextboxFieldProps extends QFCTypeExecuted {
  onBlur: () => void;
  isRequired: boolean;
  isRequiredBeforeBind?: boolean;
}

const addDollar = (value: string): string => {
  if (value !== undefined) {
    return value?.length > 0 ? `$${value}` : '';
  }
  return '';
};

const getTextboxValue = (value: string, dataType: DataType) => {
  switch (dataType) {
    case DataType.Integer:
    case DataType.Currency:
    case DataType.Float:
      return getCurrencyFormat(getFloat(value));
    default:
      return value;
  }
};

export const TextboxField = ({
  setValue,
  attributeType,
  errorMessage,
  value,
  isCollapsed,
  hoverText,
  externalIcon,
  isRequired,
  isRequiredBeforeBind,
  isReadOnly,
  onBlur,
  additionalInfoLink,
}: TextboxFieldProps) => {
  const layoutPreferences = useStore((state) => state.layoutPreferences, shallow);

  const [stateValue, setStateValue] = useState<string | null>(value?.toString() ?? null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLElement | null>(null);
  const handleOpenPopover = () => {
    setAnchorEl(inputRef.current as HTMLButtonElement);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const debouncedSetValue = useDebouncedCallback((value) => {
    setValue(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const handleChange = (newValue: string | null) => {
    setStateValue(newValue);
    debouncedSetValue(newValue);
  };

  useEffect(() => {
    setStateValue(value);
  }, [value]);

  let textboxValue =
    stateValue !== null && stateValue !== undefined
      ? getTextboxValue(stateValue.toString(), attributeType.dataType as DataType)
      : undefined;

  switch (attributeType.codeName) {
    case 'CustomerPhone':
      if (!isCollapsed) {
        return (
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'top',
              height: '100%',
              width: '100%',

              justifyContent: 'flex-end',
              verticalAlign: 'top',
            }}
          >
            <InputLabelContainer
              inputLabelProps={{
                children: attributeType.label,
                endIcon: hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null,
                tooltipTitle: hoverText,
                error: !!errorMessage,
                disabled: isReadOnly,
                required: isRequired,
                requiredBeforeBind: isRequiredBeforeBind,
              }}
              helperLabelProps={{
                disabled: isReadOnly,
              }}
              additionalInfoLink={additionalInfoLink}
            >
              <Box
                className='maskedInput'
                component={StyledIMaskInput}
                mask='+{1} (000) 000-0000'
                onAccept={(newValue) => handleChange(newValue)}
                value={textboxValue}
                hasError={!!errorMessage}
                sx={{
                  ...getMaskedStyles(!!errorMessage),
                  lineHeight: `${adjustLineHeight(16, layoutPreferences.fontSize ?? FontSize.DEFAULT)}px`,
                }}
              />
            </InputLabelContainer>
            <FormHelperText error={!!errorMessage}>{errorMessage ?? attributeType.helpText ?? ' '}</FormHelperText>
          </Box>
        );
      }
      break;
    case 'CPYearOfConstruction':
      textboxValue = textboxValue?.replace(/[^0-9]+/g, '');
      break;
    default:
      break;
  }

  if (isCollapsed) {
    if (textboxValue !== undefined && attributeType.dataType === DataType.Currency) {
      textboxValue = textboxValue !== null ? addDollar(textboxValue) : '';
    }
    return <CollapsedField label={attributeType.label} value={textboxValue} deprecated={attributeType.deprecated} />;
  }

  const textboxValueLength = textboxValue?.length ?? 0;
  const inputWidth = inputRef?.current ? inputRef?.current.offsetWidth : 100000;
  const inputCharsAllowed = Math.ceil(inputWidth / 8);
  const wordyField = textboxValueLength >= inputCharsAllowed;

  return (
    <Box
      ref={inputRef}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'top',
        height: '100%',
        width: '100%',
        justifyContent: 'flex-end',
        verticalAlign: 'top',
        '& .MuiButtonBase-root#popover-button': {
          height: 0,
          padding: 0,
        },
        '& .MuiFormHelperText-root': {
          lineHeight: `${adjustLineHeight(24, layoutPreferences.fontSize ?? FontSize.DEFAULT)}px`,
          margin: 0,
        },
      }}
    >
      <TextField
        additionalInfoLink={additionalInfoLink}
        error={!!errorMessage}
        deprecated={attributeType.deprecated}
        helperText={errorMessage ?? attributeType.helpText ?? ' '}
        onBlur={onBlur}
        type='text'
        slotProps={{
          input: {
            inputMode:
              attributeType.dataType === DataType.Float || attributeType.dataType === DataType.Currency
                ? 'decimal'
                : attributeType.dataType === DataType.Integer
                  ? 'numeric'
                  : undefined,
          },
          htmlInput: {
            maxLength: NAMED_INSURED.ATTRIBUTE_FIELDS.includes(attributeType.codeName)
              ? NAMED_INSURED.MAX_LENGTH
              : undefined,
            pattern:
              attributeType.dataType === DataType.Float ||
              attributeType.dataType === DataType.Currency ||
              attributeType.dataType === DataType.Integer
                ? '[0-9]*'
                : undefined,
          },
        }}
        startIcon={attributeType.dataType === DataType.Currency ? <DollarSign /> : null}
        endIcon={
          wordyField && !isReadOnly ? (
            <Tooltip title='Click here to expand'>
              <IconButton onClick={handleOpenPopover} size='small'>
                <Plus width='12px' height='12px' />
              </IconButton>
            </Tooltip>
          ) : null
        }
        inputLabel={attributeType.label}
        inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
        tooltipTitle={hoverText}
        externalIcon={externalIcon}
        style={{ width: '100%' }}
        value={textboxValue}
        onChange={(e) => {
          const { value } = e.target;
          const newValue = getTextboxValue(value, attributeType.dataType as DataType);
          if (
            attributeType.dataType === DataType.String ||
            !newValue.includes('-') ||
            (newValue.includes('-') && newValue[0] === '-' && (newValue.match(/-/g) || []).length <= 1)
          ) {
            if (newValue !== '') {
              handleChange(newValue);
            } else {
              handleChange(null);
            }
          }
        }}
        required={isRequired}
        requiredBeforeBind={isRequiredBeforeBind}
        disabled={isReadOnly}
      />
      {wordyField && !!anchorEl && (
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{}}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2 }}>
            <IconButton
              size='small'
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                svg: { width: '10px' },
              }}
              onClick={handleClosePopover}
            >
              <Times />
            </IconButton>
            <TextField
              fullWidth
              error={!!errorMessage}
              onBlur={onBlur}
              type='text'
              multiline
              maxRows={Infinity}
              slotProps={{
                htmlInput: {
                  maxLength: NAMED_INSURED.ATTRIBUTE_FIELDS.includes(attributeType.codeName)
                    ? NAMED_INSURED.MAX_LENGTH
                    : undefined,
                },
              }}
              inputLabel={attributeType.label}
              inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
              tooltipTitle={hoverText}
              externalIcon={externalIcon}
              helperLabel={attributeType.helpText ?? undefined}
              value={textboxValue}
              onChange={(e) => {
                const { value } = e.target;
                const newValue = getTextboxValue(value, attributeType.dataType as DataType);

                if (
                  attributeType.dataType === DataType.String ||
                  !newValue.includes('-') ||
                  (newValue.includes('-') && newValue[0] === '-' && (newValue.match(/-/g) || []).length <= 1)
                ) {
                  if (newValue !== '') {
                    handleChange(newValue);
                  } else {
                    handleChange(null);
                  }
                }
              }}
              required={isRequired}
              requiredBeforeBind={isRequiredBeforeBind}
              sx={{ width: '400px' }}
            />
            <Typography variant='caption' sx={{ color: 'text.secondary', textAlign: 'right', mt: 1 }}>
              Character count: {textboxValueLength}
              {NAMED_INSURED.ATTRIBUTE_FIELDS.includes(attributeType.codeName) &&
              textboxValueLength >= NAMED_INSURED.MAX_LENGTH
                ? ' (Max)'
                : ''}
            </Typography>
          </Box>
        </Popover>
      )}
    </Box>
  );
};
